import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sparkles, CheckCircle2, ArrowRight, Crown, X, Globe, CreditCard, Loader2, Shield } from 'lucide-react';
import LumeraLogo from '../components/LumeraLogo';
import axios from 'axios';
import { toast } from 'sonner';

import { PRO_PRICES, detectCurrency, formatPrice, getStripePlanKey } from '../data/pricing';
import { FreeVsProComparison } from '../components/FreeVsProComparison';
import { trackProUpgradeStarted, trackEvent } from '../utils/analytics';
import { PageMeta } from '../components/PageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICES_MONTHLY = { usd: { symbol: '$', amount: PRO_PRICES.monthly.usd, label: 'USD' }, nok: { symbol: 'kr', amount: PRO_PRICES.monthly.nok, label: 'NOK' } };
const PRICES_YEARLY = { usd: { symbol: '$', amount: PRO_PRICES.yearly.usd, label: 'USD' }, nok: { symbol: 'kr', amount: PRO_PRICES.yearly.nok, label: 'NOK' } };

const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.645h6.563c2.175 0 3.74.58 4.65 1.724.426.535.698 1.145.811 1.814.118.696.12 1.544-.006 2.593l-.013.085v.754l.588.332a3.5 3.5 0 0 1 1.182 1.05c.398.567.652 1.27.756 2.088.107.84.072 1.833-.104 2.95a8.9 8.9 0 0 1-.93 2.85 5.7 5.7 0 0 1-1.502 1.826c-.604.476-1.32.827-2.128 1.044a9.9 9.9 0 0 1-2.653.33h-.63a1.9 1.9 0 0 0-1.184.413 1.9 1.9 0 0 0-.655 1.105l-.032.166-.547 3.464-.025.12a.2.2 0 0 1-.056.11.16.16 0 0 1-.103.04Z" fill="#253B80"/>
    <path d="M19.542 9.318a7 7 0 0 0-.086-.493c-.96-4.917-6.324-4.917-6.324-4.917H6.471L3.592 21.176a.42.42 0 0 0 .415.487h4.2l1.055-6.682a1.24 1.24 0 0 1 1.223-1.044h2.549c5.005 0 6.074-3.842 6.508-6.619Z" fill="#179BD7"/>
    <path d="M8.047 9.15a.77.77 0 0 1 .757-.644h4.783s2.912.048 4.192 1.98c-1.032-5.288-5.312-5.358-5.312-5.358H7.12a.77.77 0 0 0-.757.645L3.592 21.177a.42.42 0 0 0 .415.486h4.2l1.055-6.682L8.047 9.15Z" fill="#222D65"/>
  </svg>
);

const Pricing = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPro, isShopifyBilled, shopifyBilling } = useAuth();
  const [currency, setCurrency] = useState(detectCurrency);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  useEffect(() => {
    trackEvent('pricing_page_viewed', { targetType: 'subscription' });
  }, []);

  const priceMap = billingInterval === 'yearly' ? PRICES_YEARLY : PRICES_MONTHLY;
  const price = priceMap[currency];
  const monthlyEquiv = billingInterval === 'yearly'
    ? (currency === 'nok' ? PRO_PRICES.yearly.monthlyEquivalentNok : PRO_PRICES.yearly.monthlyEquivalentUsd)
    : null;

  const [shopifyUpgradeError, setShopifyUpgradeError] = useState('');

  const handleShopifyUpgrade = useCallback(() => {
    // Redirect to Shopify's hosted App Pricing page
    // URL is pre-computed by the backend and stored in shopifyBilling context
    const pricingUrl = shopifyBilling.pricingUrl;
    if (!pricingUrl) {
      setShopifyUpgradeError('Shopify pricing page URL not available. Please reconnect your store from Profile → Integrations.');
      toast.error('Shopify pricing page not available.');
      return;
    }
    console.log('[Shopify] Opening hosted pricing page:', pricingUrl);
    window.location.href = pricingUrl;
  }, [shopifyBilling]);

  const handleUpgrade = useCallback(() => {
    if (!isAuthenticated) { navigate('/register'); return; }
    if (isShopifyBilled) {
      handleShopifyUpgrade();
      return;
    }
    trackProUpgradeStarted(billingInterval, price.amount, currency);
    setShowPaymentDialog(true);
  }, [isAuthenticated, isShopifyBilled, navigate, handleShopifyUpgrade, billingInterval, price, currency]);

  const handleStripeCheckout = async () => {
    setPaymentMethod('stripe');
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan: getStripePlanKey(billingInterval),
        origin_url: window.location.origin,
        currency
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Stripe checkout error:', err);
      setCheckoutLoading(false);
      setPaymentMethod(null);
    }
  };

  const handlePayPalCheckout = async () => {
    setPaymentMethod('paypal');
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/paypal/create-subscription`, {
        currency,
        return_url: `${window.location.origin}/payment/success?provider=paypal`,
        cancel_url: `${window.location.origin}/pricing`,
      });
      if (response.data.approve_url) {
        window.location.href = response.data.approve_url;
      }
    } catch (err) {
      console.error('PayPal checkout error:', err);
      setCheckoutLoading(false);
      setPaymentMethod(null);
    }
  };

  const plans = useMemo(() => [
    {
      name: 'Free',
      priceDisplay: `${PRICES_MONTHLY.usd.symbol}0`,
      period: 'forever',
      desc: 'Perfect for trying out Create by Lumera',
      cta: isAuthenticated ? 'Current Plan' : 'Get Started Free',
      ctaAction: () => isAuthenticated ? navigate('/dashboard') : navigate('/register'),
      primary: false,
      disabled: isAuthenticated && !isPro,
      features: [
        { text: '3 generations per month', included: true },
        { text: 'All 32+ product types', included: true },
        { text: 'Basic export (PNG)', included: true },
        { text: 'Personal use only', included: true, muted: true },
        { text: 'Unlimited generations', included: false },
        { text: 'Print-ready PDF export', included: false },
        { text: 'Commercial License', included: false },
        { text: 'Pinterest Pin Creator', included: false },
        { text: 'Priority support', included: false }
      ]
    },
    {
      name: 'Pro',
      priceDisplay: currency === 'nok' ? `${price.amount} ${price.symbol}` : `${price.symbol}${price.amount}`,
      period: billingInterval === 'yearly' ? '/year' : '/month',
      monthlyEquiv: monthlyEquiv ? (currency === 'nok' ? `${monthlyEquiv.toFixed(0)} kr/mo` : `$${monthlyEquiv.toFixed(2)}/mo`) : null,
      desc: 'For serious creators who sell',
      cta: isPro ? 'Your Current Plan' : (isShopifyBilled && shopifyBilling.tier === 'pro' ? 'Your Current Plan' : 'Upgrade to Pro'),
      ctaAction: isPro ? () => navigate('/dashboard') : (isShopifyBilled && shopifyBilling.tier === 'pro' ? () => navigate('/dashboard') : handleUpgrade),
      primary: true,
      disabled: isPro || (isShopifyBilled && shopifyBilling.tier === 'pro'),
      features: [
        { text: 'Unlimited generations', included: true },
        { text: 'All 32+ product types', included: true },
        { text: 'High-quality PNG export', included: true },
        { text: 'Print-ready PDF export (A4/Letter)', included: true },
        { text: 'Commercial License included', included: true, highlight: true },
        { text: 'Unlimited end-product sales', included: true, highlight: true },
        { text: 'Premium design templates', included: true },
        { text: 'Pinterest Pin Creator', included: true },
        { text: 'Priority support', included: true }
      ]
    }
  ], [isAuthenticated, isPro, isShopifyBilled, shopifyBilling, currency, price, navigate, billingInterval, monthlyEquiv, handleUpgrade]);

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-[#F7F2EC] dark:bg-background">
      <PageMeta
        title="Pricing"
        description="Three ways to use Lumera: Free with branding, buy individual products, or go Pro for full access and clean exports."
        url="/pricing"
      />
      {/* Nav */}
      <nav className="bg-[#F7F2EC]/85 dark:bg-card/90 backdrop-blur-md border-b border-[#DDD4C8]/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[56px] md:h-[68px]">
          <LumeraLogo size="default" />
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-[#4A3525] text-white hover:bg-[#3D2B1F] rounded-lg px-6 font-semibold">Dashboard</Button>
            ) : (<>
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-[#8A7D72] hover:text-[#4A3525] font-medium text-sm">Log in</Button>
              <Button onClick={() => navigate('/register')} className="bg-[#4A3525] text-white hover:bg-[#3D2B1F] rounded-lg px-7 py-2.5 font-semibold text-sm shadow-sm">Start Free</Button>
            </>)}
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-20 pb-10 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#C4A98D] mb-4 block">Pricing</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#4A3525] tracking-tight mb-4">
            Simple, <span className="italic">transparent</span> pricing
          </h1>
          <p className="text-base md:text-lg text-[#8A7D72] max-w-xl mx-auto">
            Start free and upgrade when you&apos;re ready to scale your digital product business.
          </p>
        </div>
      </section>

      {/* Shopify Billing Notice */}
      {isShopifyBilled && (
        <div data-testid="shopify-billing-notice" className="max-w-3xl mx-auto px-6 mb-8">
          <div className="bg-[#F0F7F0] border border-[#C8E6C8] rounded-xl px-6 py-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#5E8E3E"><path d="M15.3 2.4l-.4 1.3c-.1.3-.3.5-.6.6-.3.1-.6.1-.9 0-.6-.3-1-.4-1.5-.4h-.1c-1.8.1-3.1 1.5-3.2 1.5l-.1.1-1-.2c.1-.1 1.8-1.9 4.2-2h.2c.7 0 1.3.2 2 .5l.5-1.5 1-.1.9.2zm1.5 3.5c-1.5-1.6-3.7-1.4-3.7-1.4l-.8.1c-.9.2-1.7.6-2.3 1.3-.8.8-1.2 1.9-1.3 3.1l-1.8 10.6 4.3.7.5-2.8c.3.1.6.2 1 .2 2.8 0 5.1-2.4 5.6-5.6.4-2.5-.2-4.6-1.5-6.2z"/></svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2E5A1E] mb-1">Your billing is managed through Shopify</p>
              <p className="text-xs text-[#5E8E3E]">
                {shopifyBilling.tier === 'pro' 
                  ? `You're on the Pro plan via Shopify (${shopifyBilling.shopName || shopifyBilling.shopDomain}). Manage your subscription in your Shopify admin.`
                  : `Connected to Shopify store "${shopifyBilling.shopName || shopifyBilling.shopDomain}". Click Upgrade to Pro below to subscribe through Shopify.`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Shopify upgrade error */}
      {shopifyUpgradeError && (
        <div data-testid="shopify-upgrade-error" className="max-w-3xl mx-auto px-6 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 flex items-start gap-3">
            <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800 mb-1">Could not start Shopify billing</p>
              <p className="text-xs text-red-600">{shopifyUpgradeError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Billing interval toggle */}
      <div className="flex justify-center mb-4">
        <div data-testid="billing-toggle" className="inline-flex items-center gap-1 bg-white rounded-lg p-1 border border-[#DDD4C8] shadow-sm">
          <button
            data-testid="billing-monthly"
            onClick={() => setBillingInterval('monthly')}
            className={`px-5 py-2 rounded-md text-sm font-semibold transition-all duration-300 ${
              billingInterval === 'monthly'
                ? 'bg-[#4A3525] text-white shadow-sm'
                : 'text-[#8A7D72] hover:text-[#4A3525]'
            }`}
          >
            Monthly
          </button>
          <button
            data-testid="billing-yearly"
            onClick={() => setBillingInterval('yearly')}
            className={`px-5 py-2 rounded-md text-sm font-semibold transition-all duration-300 relative ${
              billingInterval === 'yearly'
                ? 'bg-[#4A3525] text-white shadow-sm'
                : 'text-[#8A7D72] hover:text-[#4A3525]'
            }`}
          >
            Yearly
            <span className="ml-1.5 inline-block text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700">
              Save {PRO_PRICES.yearly.savingsPercent}%
            </span>
          </button>
        </div>
      </div>

      {/* Currency toggle */}
      <div className="flex justify-center mb-10">
        <div data-testid="currency-toggle" className="inline-flex items-center gap-1 bg-white rounded-lg p-1 border border-[#DDD4C8] shadow-sm">
          <Globe className="w-4 h-4 text-[#8A7D72] ml-2.5" />
          {Object.entries(PRICES_MONTHLY).map(([key, val]) => (
            <button
              key={key}
              data-testid={`currency-${key}`}
              onClick={() => setCurrency(key)}
              className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all duration-300 ${
                currency === key
                  ? 'bg-stone-900 text-white shadow-sm'
                  : 'text-stone-500 hover:text-stone-800'
              }`}
            >
              {val.label}
            </button>
          ))}
        </div>
      </div>

      {/* Plans */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-8">
          {plans.map((plan) => (
            <div key={plan.name} data-testid={`plan-${plan.name.toLowerCase()}`} className={`bg-white p-8 md:p-10 rounded-xl border shadow-lg relative overflow-hidden ${
              plan.primary ? 'border-[#4A3525]/20 shadow-[0_8px_40px_rgba(42,38,38,0.1)]' : 'border-[#DDD4C8]'
            }`}>
              {plan.primary && (
                <div className="absolute top-0 left-0 right-0 bg-[#4A3525] text-white text-center text-[10px] font-bold tracking-[0.12em] py-1.5">MOST POPULAR</div>
              )}
              <div className={plan.primary ? 'mt-4' : ''}>
                <div className="flex items-center gap-2 mb-4">
                  {plan.primary && <Crown className="w-5 h-5 text-[#A89B8E]" strokeWidth={1.5} />}
                  <h3 className="text-xl font-bold text-[#4A3525]">{plan.name}</h3>
                </div>
                <div className="flex items-end gap-1 mb-2">
                  <span className="text-5xl font-bold text-[#4A3525]" style={{ fontFamily: "'Playfair Display', serif" }}>{plan.priceDisplay}</span>
                  <span className="text-[#8A7D72] mb-1">{plan.period}</span>
                </div>
                {plan.monthlyEquiv && (
                  <p className="text-sm text-emerald-600 font-medium -mt-1 mb-2">{plan.monthlyEquiv}</p>
                )}
                <p className="text-[#8A7D72] mb-8">{plan.desc}</p>

                <Button
                  data-testid={`plan-${plan.name.toLowerCase()}-btn`}
                  onClick={plan.ctaAction}
                  disabled={plan.disabled}
                  className={`w-full rounded-lg py-6 font-semibold text-base transition-all duration-300 ${
                    plan.primary
                      ? 'bg-[#4A3525] text-white hover:bg-[#3D2B1F] shadow-lg hover:shadow-xl hover:-translate-y-[1px]'
                      : 'bg-white text-[#4A3525] border border-[#DDD4C8] hover:border-[#A89B8E]/50 hover:bg-[#F7F2EC]'
                  }`}
                >
                  {plan.cta} {!plan.disabled && <ArrowRight className="ml-2 w-4 h-4" />}
                </Button>

                <div className="mt-8 space-y-3">
                  {plan.features.map((feature) => (
                    <div key={feature.text} className="flex items-center gap-3">
                      {feature.included ? (
                        feature.highlight ? (
                          <Shield className="w-5 h-5 text-[#C4A98D] flex-shrink-0" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5 text-[#A89B8E] flex-shrink-0" />
                        )
                      ) : (
                        <X className="w-5 h-5 text-stone-300 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${
                        feature.highlight ? 'text-[#4A3525] font-semibold' :
                        feature.muted ? 'text-[#A89B8E] italic' :
                        feature.included ? 'text-[#8A7D72]' : 'text-stone-400'
                      }`}>{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-semibold text-[#4A3525] mb-4">100% satisfaction guaranteed</h2>
          <p className="text-[#8A7D72]">If you&apos;re not happy with Pro, cancel anytime. No questions asked. Your free plan stays active forever.</p>
        </div>
      </section>

      {/* Three ways to use Lumera */}
      <section data-testid="pricing-three-ways" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#4A3525] text-center mb-10">Three ways to use Lumera</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Free */}
            <div data-testid="pricing-free-tier" className="border border-stone-100 rounded-xl p-6 bg-[#FDFBF8]">
              <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Free</span>
              <h3 className="font-serif font-bold text-stone-900 text-lg mt-2 mb-3">Try before you buy</h3>
              <ul className="space-y-2 text-xs text-stone-600">
                <li className="flex items-start gap-2"><span className="text-stone-400 mt-0.5">·</span>Preview any product for free</li>
                <li className="flex items-start gap-2"><span className="text-stone-400 mt-0.5">·</span>See your personalized result</li>
                <li className="flex items-start gap-2"><span className="text-stone-400 mt-0.5">·</span>Exports include a small Lumera footer</li>
                <li className="flex items-start gap-2"><span className="text-stone-400 mt-0.5">·</span>3 free generations per month</li>
              </ul>
            </div>
            {/* Buy Once */}
            <div data-testid="pricing-buy-once" className="border-2 border-stone-200 rounded-xl p-6 bg-white relative">
              <span className="text-[10px] font-bold text-[#B49A88] uppercase tracking-widest">Buy Once</span>
              <h3 className="font-serif font-bold text-stone-900 text-lg mt-2 mb-3">Own it forever</h3>
              <ul className="space-y-2 text-xs text-stone-600">
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">·</span>Full access to the product you bought</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">·</span>Clean, unbranded export</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">·</span>No subscription required</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">·</span>Keep it in My Products forever</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">·</span>Prices from $4.99</li>
              </ul>
            </div>
            {/* Pro */}
            <div data-testid="pricing-pro-tier" className="border border-stone-800 rounded-xl p-6 bg-stone-900 text-white">
              <span className="text-[10px] font-bold text-[#B49A88] uppercase tracking-widest">Pro</span>
              <h3 className="font-serif font-bold text-white text-lg mt-2 mb-3">Unlimited access</h3>
              <ul className="space-y-2 text-xs text-stone-300">
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">·</span>Everything in Buy Once</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">·</span>All 32+ product types included</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">·</span>Unlimited generations</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">·</span>Clean exports on everything</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">·</span>Commercial license</li>
                <li className="flex items-start gap-2"><span className="text-emerald-400 mt-0.5">·</span>Best value for regular use</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Free vs Pro Comparison */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 pb-20">
        <FreeVsProComparison />
      </div>

      {/* Footer */}
      <footer className="py-12 border-t border-[#d8cfc8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/lumera-logo.png" alt="Lumera" className="w-6 h-6 rounded-full" />
            <span className="font-serif font-semibold text-foreground">Lumera</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="text-xs font-sans text-[#6F6964] hover:text-[#B49A88] transition-colors">Privacy</a>
            <a href="/terms" className="text-xs font-sans text-[#6F6964] hover:text-[#B49A88] transition-colors">Terms</a>
            <a href="/subscription-terms" className="text-xs font-sans text-[#6F6964] hover:text-[#B49A88] transition-colors">Subscription</a>
            <a href="/refund-policy" className="text-xs font-sans text-[#6F6964] hover:text-[#B49A88] transition-colors">Refunds</a>
            <a href="/contact" className="text-xs font-sans text-[#6F6964] hover:text-[#B49A88] transition-colors">Contact</a>
          </div>
        </div>
      </footer>

      {/* Payment Method Dialog — only for non-Shopify direct users */}
      {!isShopifyBilled && (
      <Dialog open={showPaymentDialog} onOpenChange={(open) => { if (!checkoutLoading) { setShowPaymentDialog(open); setPaymentMethod(null); } }}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl border-stone-100 p-0 overflow-hidden">
          <div className="bg-stone-900 px-6 py-5">
            <DialogHeader>
              <DialogTitle className="text-white font-serif text-xl">Choose payment method</DialogTitle>
              <DialogDescription className="text-stone-400 font-sans text-sm mt-1">
                Pro Plan &mdash; {currency === 'nok' ? `${price.amount} ${price.symbol}` : `${price.symbol}${price.amount}`}/{billingInterval === 'yearly' ? 'year' : 'month'}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-6 space-y-3">
            {/* Stripe Option */}
            <button
              data-testid="payment-method-stripe"
              onClick={handleStripeCheckout}
              disabled={checkoutLoading}
              className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left group ${
                paymentMethod === 'stripe'
                  ? 'border-primary bg-primary/5'
                  : 'border-stone-100 hover:border-stone-300 bg-white'
              } ${checkoutLoading && paymentMethod !== 'stripe' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-[#635BFF]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-stone-800 text-sm">Credit / Debit Card</p>
                <p className="font-sans text-xs text-stone-500 mt-0.5">Visa, Mastercard, Amex &mdash; via Stripe</p>
              </div>
              {checkoutLoading && paymentMethod === 'stripe' ? (
                <Loader2 className="w-5 h-5 text-primary animate-spin flex-shrink-0" />
              ) : (
                <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 transition-colors flex-shrink-0" />
              )}
            </button>

            {/* PayPal Option */}
            <button
              data-testid="payment-method-paypal"
              onClick={handlePayPalCheckout}
              disabled={checkoutLoading}
              className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left group ${
                paymentMethod === 'paypal'
                  ? 'border-primary bg-primary/5'
                  : 'border-stone-100 hover:border-stone-300 bg-white'
              } ${checkoutLoading && paymentMethod !== 'paypal' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#0070BA]/10 flex items-center justify-center flex-shrink-0">
                <PayPalIcon />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-stone-800 text-sm">PayPal</p>
                <p className="font-sans text-xs text-stone-500 mt-0.5">Pay with your PayPal balance or linked card</p>
              </div>
              {checkoutLoading && paymentMethod === 'paypal' ? (
                <Loader2 className="w-5 h-5 text-primary animate-spin flex-shrink-0" />
              ) : (
                <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 transition-colors flex-shrink-0" />
              )}
            </button>

            <div className="flex items-center justify-center gap-2 pt-3">
              <Shield className="w-3.5 h-3.5 text-stone-400" />
              <p className="text-[11px] font-sans text-stone-400">Secure, encrypted payment. Cancel anytime.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
};

export default Pricing;
